import type { ColumnDef } from '@tanstack/react-table'
import { Fragment, type ReactNode, useContext } from 'react'
import { useInView } from 'react-intersection-observer'

import AtomVirtualizedTable from '@/components/Atoms/AtomVirtualizedTable/AtomVirtualizedTable'
import { TABLE_EMPTY_TEXT } from '@/constants/ui'
import { useChunkedList } from '@/hooks/ui/useChunkedList'
import { ScreenSizeContext } from '@/providers/ScreenSizeProvider'

import cn from './CatalogApprovalTable.module.scss'

const ROWS_CHUNK_SIZE = 60
const LOAD_MORE_ROOT_MARGIN = '240px'

export type CatalogApprovalColumn<T> = ColumnDef<T, unknown> & {
  id: string
  width: string
}

type CatalogApprovalTableProps<T> = {
  ariaLabel: string
  columns: CatalogApprovalColumn<T>[]
  data: T[]
  rowKey: (record: T) => string
  getRowClassName?: (record: T) => string | undefined
  estimateRowHeight?: number
  renderMobileCard?: (record: T) => ReactNode
  isOrganisationSelected?: boolean
}

function CatalogApprovalTable<T>({
  ariaLabel,
  columns,
  data,
  rowKey,
  getRowClassName,
  estimateRowHeight,
  renderMobileCard,
  isOrganisationSelected = false,
}: CatalogApprovalTableProps<T>) {
  const screen = useContext(ScreenSizeContext)
  const isMobile = Boolean(screen?.xs || screen?.sm)
  const { visibleItems, hasMore, showMore } = useChunkedList(
    data,
    ROWS_CHUNK_SIZE
  )
  const { ref: loadMoreRef } = useInView({
    rootMargin: LOAD_MORE_ROOT_MARGIN,
    onChange: (inView) => {
      if (inView && hasMore) showMore()
    },
  })

  if (isMobile && renderMobileCard) {
    if (visibleItems.length === 0)
      return <div className={cn.empty}>{TABLE_EMPTY_TEXT}</div>
    return (
      <div className={cn.mobileCards}>
        {visibleItems.map((record) => (
          <Fragment key={rowKey(record)}>{renderMobileCard(record)}</Fragment>
        ))}
        {hasMore && <div ref={loadMoreRef} className={cn.loadMore} />}
      </div>
    )
  }

  return (
    <AtomVirtualizedTable
      ariaLabel={ariaLabel}
      data={visibleItems}
      columns={columns}
      columnWidths={Object.fromEntries(
        columns.map((column) => [column.id, column.width])
      )}
      getRowId={rowKey}
      getRowClassName={getRowClassName}
      estimateRowHeight={estimateRowHeight}
      hasFloatingHeader
      isOrganisationSelected={isOrganisationSelected}
      infiniteScroll={{
        hasNextPage: hasMore,
        isFetchingNextPage: false,
        onLoadMore: showMore,
      }}
    />
  )
}

export default CatalogApprovalTable
